import React from 'react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card } from '../components/ui/card';
import { FileText, Shield, AlertCircle, DollarSign, Ban, Users, Scale, Info } from 'lucide-react';

export const TermsConditionsPage = () => {
  const sections = [
    {
      icon: FileText,
      title: "Agreement to Terms",
      content: [
        {
          subtitle: "Acceptance of Terms",
          text: "By accessing or using the Trademark Fashion Brand website and services, you agree to be bound by these Terms and Conditions, our Privacy Policy, and all applicable laws and regulations. If you do not agree with any part of these terms, you may not use our services."
        },
        {
          subtitle: "Eligibility",
          text: "You must be at least 18 years old and legally capable of entering into binding contracts to use our services. By using our services, you represent and warrant that you meet these eligibility requirements."
        },
        {
          subtitle: "Modifications to Terms",
          text: "We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting on our website with an updated date. Your continued use of our services after changes are posted constitutes acceptance of the modified terms. We encourage you to review these terms periodically."
        }
      ]
    },
    {
      icon: Shield,
      title: "Services Provided",
      content: [
        {
          subtitle: "Trademark Registration Services",
          text: "Trademark Fashion Brand provides trademark registration services including trademark searches, USPTO application preparation and filing, office action responses, trademark monitoring, and related advisory services. Our services are limited to trademark registration assistance and do not constitute legal representation unless you specifically retain one of our attorneys for legal services."
        },
        {
          subtitle: "Not Legal Advice",
          text: "The information provided on our website and through our services is for general informational purposes only and does not constitute legal advice. While our team includes experienced trademark professionals and USPTO-registered attorneys, the general information we provide should not be relied upon as legal advice for your specific situation. For legal advice, you should consult with a licensed attorney."
        },
        {
          subtitle: "No Guarantee of Registration",
          text: "While we strive for high success rates, we cannot guarantee that the USPTO will approve your trademark application. Trademark registration is ultimately determined by USPTO examining attorneys who evaluate applications based on federal trademark law. Factors outside our control, including existing conflicting marks, descriptiveness issues, or USPTO policy changes, may result in refusal of your application."
        },
        {
          subtitle: "USPTO Independence",
          text: "Trademark Fashion Brand is an independent service provider and is not affiliated with, endorsed by, or connected to the United States Patent and Trademark Office (USPTO). All trademark applications are subject to USPTO examination and approval processes."
        }
      ]
    },
    {
      icon: DollarSign,
      title: "Fees and Payment",
      content: [
        {
          subtitle: "Service Fees",
          text: "Our service fees are clearly outlined in our pricing plans and must be paid before we begin work on your trademark application. Service fees are separate from and do not include USPTO government filing fees, which are paid directly to the USPTO and are non-refundable."
        },
        {
          subtitle: "Government Filing Fees",
          text: "USPTO government filing fees are determined by the USPTO and are subject to change. Current government fees are $250-$350 per class of goods/services. These fees are paid directly to the USPTO when your application is filed and are non-refundable regardless of application outcome."
        },
        {
          subtitle: "Payment Terms",
          text: "Payment is due upon ordering services unless otherwise agreed in writing. We accept major credit cards and other payment methods as indicated on our website. By providing payment information, you authorize us to charge your payment method for services ordered."
        },
        {
          subtitle: "Refund Policy",
          text: "Our service fees are generally non-refundable once we have begun work on your application. However, if we have not yet filed your application with the USPTO, you may request a refund minus any work completed and administrative costs. Government filing fees paid to the USPTO are never refundable under any circumstances. Refund requests must be submitted in writing."
        },
        {
          subtitle: "Additional Fees",
          text: "Additional fees may apply for services beyond the initial application filing, including but not limited to office action responses, amendments, statements of use, renewals, and oppositions. You will be notified of any additional fees before we perform additional services."
        }
      ]
    },
    {
      icon: Users,
      title: "Client Responsibilities",
      content: [
        {
          subtitle: "Accurate Information",
          text: "You are responsible for providing accurate, complete, and truthful information for your trademark application. This includes your contact information, business details, mark description, goods/services descriptions, and filing basis information. Providing false or misleading information may result in denial of your application or cancellation of your registration."
        },
        {
          subtitle: "Timely Responses",
          text: "You must respond promptly to requests for information or decisions from us or the USPTO. Failure to respond within required deadlines may result in abandonment of your application or cancellation of your registration. We are not responsible for consequences resulting from your failure to respond timely."
        },
        {
          subtitle: "Use of Mark",
          text: "You represent and warrant that you have the right to use the mark you are seeking to register, that your use of the mark does not infringe upon the rights of any third party, and that all information regarding your use of the mark is accurate and truthful."
        },
        {
          subtitle: "Maintenance Requirements",
          text: "You are responsible for maintaining your trademark registration through required filings with the USPTO, including Declarations of Use (between years 5-6 and 9-10) and renewals every 10 years. While we offer reminder and filing services, ultimate responsibility for maintaining your registration rests with you."
        }
      ]
    },
    {
      icon: Ban,
      title: "Prohibited Uses",
      content: [
        {
          subtitle: "Unlawful Activities",
          text: "You may not use our services for any unlawful purpose or in violation of any applicable local, state, federal, or international law. This includes attempting to register trademarks you do not have rights to use or that infringe upon others' intellectual property rights."
        },
        {
          subtitle: "Fraudulent Applications",
          text: "You may not submit false or fraudulent information in trademark applications. Doing so may result in denial of your application, cancellation of your registration, and potential legal consequences."
        },
        {
          subtitle: "System Abuse",
          text: "You may not attempt to interfere with, disrupt, or create undue burden on our website or systems, including through automated means, viruses, or any other harmful code."
        },
        {
          subtitle: "Unauthorized Access",
          text: "You may not attempt to gain unauthorized access to our systems, networks, or data, or access accounts or information belonging to other clients."
        }
      ]
    },
    {
      icon: AlertCircle,
      title: "Limitation of Liability",
      content: [
        {
          subtitle: "Service Limitations",
          text: "Our services are provided on an 'as is' and 'as available' basis. We make no warranties, express or implied, regarding the availability, accuracy, reliability, or results of our services. We do not warrant that our services will be uninterrupted, secure, or error-free."
        },
        {
          subtitle: "Disclaimer of Warranties",
          text: "To the fullest extent permitted by law, Trademark Fashion Brand disclaims all warranties, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant or guarantee approval of your trademark application."
        },
        {
          subtitle: "Limitation of Damages",
          text: "In no event shall Trademark Fashion Brand, its officers, directors, employees, or agents be liable for any indirect, incidental, special, consequential, or punitive damages, including lost profits, lost revenue, lost data, or business interruption, arising from or related to your use of our services, even if we have been advised of the possibility of such damages."
        },
        {
          subtitle: "Maximum Liability",
          text: "Our total liability to you for any claims arising from or related to our services shall not exceed the amount of fees you paid to us for services in the 12 months preceding the claim. Some jurisdictions do not allow the exclusion or limitation of liability for consequential or incidental damages, so the above limitation may not apply to you."
        }
      ]
    },
    {
      icon: Scale,
      title: "Dispute Resolution",
      content: [
        {
          subtitle: "Informal Resolution",
          text: "If you have a dispute with us, you agree to first contact us and attempt to resolve the dispute informally by sending written notice describing the nature of the dispute and desired resolution. We will attempt to resolve the dispute in good faith."
        },
        {
          subtitle: "Arbitration Agreement",
          text: "If we cannot resolve a dispute informally within 30 days, any remaining dispute shall be resolved through binding arbitration in accordance with the Commercial Arbitration Rules of the American Arbitration Association. Arbitration will take place in San Diego, California, unless both parties agree to a different location."
        },
        {
          subtitle: "Class Action Waiver",
          text: "You agree that any arbitration or legal proceeding shall be conducted on an individual basis and not as a class action, consolidated action, or representative action. You waive any right to participate in a class action lawsuit or class-wide arbitration."
        },
        {
          subtitle: "Governing Law",
          text: "These Terms and Conditions shall be governed by and construed in accordance with the laws of the State of California, without regard to its conflict of law provisions. You agree to submit to the personal jurisdiction of the courts located in San Diego County, California for any disputes not subject to arbitration."
        }
      ]
    },
    {
      icon: Info,
      title: "General Provisions",
      content: [
        {
          subtitle: "Entire Agreement",
          text: "These Terms and Conditions, together with our Privacy Policy and any service-specific agreements, constitute the entire agreement between you and Trademark Fashion Brand regarding our services and supersede all prior agreements and understandings, whether written or oral."
        },
        {
          subtitle: "Severability",
          text: "If any provision of these Terms and Conditions is found to be invalid or unenforceable by a court of competent jurisdiction, the remaining provisions shall continue in full force and effect."
        },
        {
          subtitle: "Waiver",
          text: "Our failure to enforce any right or provision of these Terms and Conditions shall not be deemed a waiver of such right or provision. Any waiver must be in writing and signed by an authorized representative."
        },
        {
          subtitle: "Assignment",
          text: "You may not assign or transfer these Terms and Conditions or your rights hereunder without our prior written consent. We may assign our rights and obligations under these terms without restriction."
        },
        {
          subtitle: "Survival",
          text: "Provisions of these Terms and Conditions that by their nature should survive termination shall survive, including but not limited to limitation of liability, dispute resolution, and general provisions."
        }
      ]
    }
  ];

  return (
    <>
      <SEO 
        title="Terms & Conditions | Trademark Fashion Brand - Service Agreement"
        description="Read our Terms and Conditions governing trademark registration services. Understand your rights, responsibilities, service limitations, fees, refund policy, and legal agreements with Trademark Fashion Brand."
        keywords="terms and conditions, service agreement, trademark terms, legal agreement, service limitations, refund policy, client responsibilities, dispute resolution"
        canonical="/terms-conditions"
      />
      <div className="min-h-screen bg-[#FFF7ED]">
        <Header openPopup={() => {}} />
      
      <div className="pt-24 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Page Header */}
            <div className="text-center mb-16">
              <h1 className="text-5xl md:text-6xl font-bold text-[#7C2D12] mb-6">
                Terms & Conditions
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-4">
                Please read these Terms and Conditions carefully before using our trademark registration services.
              </p>
              <p className="text-sm text-gray-500">
                Last Updated: December 2024
              </p>
            </div>

            {/* Introduction */}
            <Card className="p-8 bg-white border-2 border-[#FED7AA] mb-12">
              <p className="text-gray-700 leading-relaxed text-lg mb-4">
                These Terms and Conditions ("Terms") govern your use of the Trademark Fashion Brand website and services. By accessing or using our website or services, you agree to be bound by these Terms.
              </p>
              <p className="text-gray-700 leading-relaxed text-lg">
                Please read these Terms carefully. If you do not agree to these Terms, you should not use our website or services. These Terms constitute a legally binding agreement between you and Trademark Fashion Brand.
              </p>
            </Card>

            {/* Main Content Sections */}
            <div className="space-y-12">
              {sections.map((section, index) => {
                const IconComponent = section.icon;
                return (
                  <Card key={index} className="p-8 bg-white border-2 border-[#FED7AA] hover:border-[#F97316] transition-all duration-300">
                    <div className="flex items-start mb-6">
                      <div className="bg-[#F97316] p-3 rounded-lg mr-4">
                        <IconComponent className="text-white" size={28} />
                      </div>
                      <h2 className="text-3xl font-bold text-[#7C2D12] mt-1">
                        {section.title}
                      </h2>
                    </div>
                    
                    <div className="space-y-6">
                      {section.content.map((item, idx) => (
                        <div key={idx}>
                          <h3 className="text-xl font-semibold text-[#7C2D12] mb-3">
                            {item.subtitle}
                          </h3>
                          <p className="text-gray-700 leading-relaxed">
                            {item.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Contact Section */}
            <Card className="p-8 bg-gradient-to-r from-[#7C2D12] to-[#F97316] border-0 mt-12">
              <h2 className="text-3xl font-bold text-white mb-4">
                Questions About These Terms?
              </h2>
              <p className="text-white/95 text-lg leading-relaxed mb-4">
                If you have questions or concerns about these Terms and Conditions, please contact us:
              </p>
              <div className="text-white/95 space-y-2">
                <p><strong>Email:</strong> info@trademarkfashionbrand.com</p>
                <p><strong>Phone:</strong> +1 (123) 123-4567</p>
                <p><strong>Mail:</strong> 10815 Rancho Bernardo Rd #300, San Diego, CA 92127</p>
              </div>
              <p className="text-white/90 text-sm mt-6">
                We will respond to your inquiry within 5 business days. For urgent matters, please call us during business hours.
              </p>
            </Card>

            {/* Acknowledgment */}
            <Card className="p-8 bg-[#FFF7ED] border-2 border-[#FED7AA] mt-8">
              <h2 className="text-2xl font-bold text-[#7C2D12] mb-4">
                Acknowledgment
              </h2>
              <p className="text-gray-700 leading-relaxed">
                By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. You also acknowledge that you have read and understand our Privacy Policy. If you have questions about these Terms, please contact us before using our services.
              </p>
            </Card>
          </div>
        </div>
      </div>

      <Footer openPopup={() => {}} />
      </div>
    </>
  );
};

export default TermsConditionsPage;
